/**
 * JInspectorState — factory class for JInspector field state.
 * Created from an entry object via constructor — single source of truth for defaults.
 * Use isDirty(current) to compare against a second JInspectorState instance.
 *
 * Field names match the entry/BE shape 1:1 (type, isPinned) — no remapping needed
 * anywhere a JInspectorState is constructed, whether from a real entry or from local
 * component state.
 */
export default class JInspectorState {
    /**
     * Creates a state snapshot from an entry object.
     * All fields fall back to safe defaults when entry is null or undefined.
     * @param {object|null} entry - journal entry object
     */
    constructor(entry) {
        this.title = entry?.title ?? '';
        this.stage = entry?.stage ?? '';
        this.importanceId = entry?.importanceId ?? 0;
        this.prioId = entry?.prioId ?? 0;
        // CHANGED — was this.entryType = entry?.type, remapped to entry.type everywhere it
        // was constructed from local state. Now named `type` directly, matching entry.type.
        this.type = entry?.type ?? '';
        this.cat = entry?.cat ?? '';
        this.stateId = entry?.stateId ?? 0;
        // ADDED — service field for move-to-topic support
        this.service = entry?.service ?? '';
        // ADDED — format field for card template identifier
        this.format = entry?.format ?? '';
        // ADDED — remark field, free text note on the entry
        this.remark = entry?.remark ?? '';
        // ADDED — tid field, editable template id
        this.tid = entry?.tid ?? '';
        // CHANGED — was this.pinned = entry?.pinned (wrong field name, always missed on a
        // real entry — see pinned/isPinned bugfix). Now named `isPinned` directly, matching
        // entry.isPinned, so no remapping needed anywhere a JInspectorState is built.
        this.isPinned = entry?.isPinned ?? false;
    }

    /**
     * Compares this snapshot against a current state instance.
     * Returns true if any field differs from the original entry values.
     * @param {JInspectorState} current - current state to compare against
     * @returns {boolean}
     */
    isDirty(current) {
        return (
            this.title !== current.title ||
            this.stage !== current.stage ||
            this.importanceId !== current.importanceId ||
            this.prioId !== current.prioId ||
            this.type !== current.type ||
            this.cat !== current.cat ||
            this.stateId !== current.stateId ||
            // ADDED
            this.service !== current.service ||
            // ADDED
            this.format !== current.format ||
            // ADDED
            this.remark !== current.remark ||
            // ADDED
            this.tid !== current.tid ||
            this.isPinned !== current.isPinned
        );
    }
}
